/**
 * DESIGN SYSTEM STEP 2 — the editor side of the printer upgrade. Every number
 * comes from lib/decor/decorStyle.ts, which the print reads too (parity). Both
 * nodes are pointer-events:none, so they never catch a tap meant for a photo.
 */
import type { CSSProperties } from 'react';
import { SHADOW, backgroundBoxPct, mmToCqi, shadowable, type DecorElement } from '../../lib/decor/decorStyle';
import { TintFilter, tintCss } from './TintFilter';

/** The page's paper, under photos and elements. Cover-fitted to the BLEED box
 *  and clipped by the page — the same crop the print makes. */
export function PageBackgroundLayer({ background }: {
  /** `tint`: a TINTED paper (lib/decor/tint.ts — 4-10's speckled paper in the album's colour). */
  background?: { imageUrl?: string | null; previewUrl?: string | null; color?: string | null; tint?: readonly string[] | null } | null;
}) {
  if (!background || (!background.imageUrl && !background.color)) return null;
  const src = background.previewUrl || background.imageUrl;
  const b = backgroundBoxPct();
  return (
    <div
      aria-hidden
      data-page-background
      className="absolute inset-0 overflow-hidden pointer-events-none"
      style={{ zIndex: 0, background: background.color ?? undefined }}
    >
      {src && <TintFilter stops={background.tint} />}
      {src && (
        <img
          src={src}
          alt=""
          draggable={false}
          style={{
            position: 'absolute',
            left: `${b.left}%`,
            top: `${b.top}%`,
            width: `${b.width}%`,
            height: `${b.height}%`,
            maxWidth: 'none',
            objectFit: 'cover',
            ...tintCss(background.tint),
          }}
        />
      )}
    </div>
  );
}

export type ShadowElement = DecorElement & {
  x: number; y: number; width: number; height: number;
  rotation?: number; flipH?: boolean; flipV?: boolean;
};

/** The soft shadow of one element: placed exactly like the element, blurred in
 *  its own frame, then turned/mirrored with it, then shifted in page space —
 *  the order the print uses (pdfDecor.drawElementShadow). */
export function ElementShadow({ el, imageSrc }: { el: ShadowElement; imageSrc?: string | null }) {
  if (!shadowable(el)) return null;
  const shift = `translate(${mmToCqi(SHADOW.offsetXMm)}cqi, ${mmToCqi(SHADOW.offsetYMm)}cqi)`;
  const rot = el.rotation ? `rotate(${el.rotation}deg)` : '';
  const flip = (el.flipH || el.flipV) ? `scaleX(${el.flipH ? -1 : 1}) scaleY(${el.flipV ? -1 : 1})` : '';
  const silhouette = el.type === 'icon' ? (imageSrc ?? el.previewUrl ?? el.imageUrl) : el.maskUrl;
  const size = el.type === 'icon' && el.fit !== 'fill' ? 'contain' : '100% 100%';
  const maskCss: CSSProperties = silhouette
    ? {
        maskImage: `url("${silhouette}")`,
        WebkitMaskImage: `url("${silhouette}")`,
        maskSize: size,
        WebkitMaskSize: size,
        maskPosition: 'center',
        WebkitMaskPosition: 'center',
        maskRepeat: 'no-repeat',
        WebkitMaskRepeat: 'no-repeat',
      }
    : {};
  return (
    <div
      aria-hidden
      data-element-shadow={el.id}
      className="absolute pointer-events-none"
      style={{
        left: `${el.x}%`,
        top: `${el.y}%`,
        width: `${el.width}%`,
        height: `${el.height}%`,
        zIndex: 1010,
        transform: [shift, rot, flip].filter(Boolean).join(' '),
        transformOrigin: 'center',
        filter: `blur(${mmToCqi(SHADOW.sigmaMm)}cqi)`,
        opacity: SHADOW.opacity,
      }}
    >
      <div style={{ width: '100%', height: '100%', background: SHADOW.color, ...maskCss }} />
    </div>
  );
}

/** CSS for a photo element's torn-edge mask (stretched to the box, like the
 *  print's /SMask). Undefined when the photo has no mask. */
export function photoMaskStyle(maskUrl?: string | null): CSSProperties | undefined {
  if (!maskUrl) return undefined;
  return {
    maskImage: `url("${maskUrl}")`,
    WebkitMaskImage: `url("${maskUrl}")`,
    maskSize: '100% 100%',
    WebkitMaskSize: '100% 100%',
    maskRepeat: 'no-repeat',
    WebkitMaskRepeat: 'no-repeat',
  };
}
